<template>
  <div class="slot-pricing-editor">
    <!-- Enable toggle -->
    <div class="toggle-row mb-3">
      <el-switch active-color="#e14eca" :value="slotData.is_custom_pricing_enabled" @change="setField('is_custom_pricing_enabled', $event)" />
      <label class="mb-0 ml-2 text-white" style="cursor: pointer" @click="setField('is_custom_pricing_enabled', !slotData.is_custom_pricing_enabled)">
        Enable custom pricing
      </label>
    </div>
    <info-hint v-if="!slotData.is_custom_pricing_enabled" variant="primary">
      Slots created from this template will use the default pricing from the event.
    </info-hint>

    <template v-if="slotData.is_custom_pricing_enabled">
      <!-- Pricing model -->
      <drawer-section title="Pricing model" icon="icon-coins">
        <div class="form-row">
          <div class="form-col">
            <base-input label="Pricing type">
              <el-select :value="slotData.pricing_type" @input="setField('pricing_type', $event)" class="select-primary" value-key="id" placeholder="Select pricing type">
                <el-option v-for="(pt, i) in pricingTypes" class="select-primary" :label="pt.name" :value="{ id: pt.id, name: pt.name }" :key="'PT' + i" />
              </el-select>
            </base-input>
          </div>
          <div class="form-col">
            <label class="text-white">Reservation style
              <el-popover popper-class="otc-pop" trigger="hover" placement="top">
                <div><div class="popover-body">A private booking reserves the entire event or time slot for a single group, while a public booking allows multiple unrelated groups to book into the same event or slot.</div></div>
                <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
              </el-popover>
            </label>
            <base-input>
              <el-select :value="slotData.is_public_booking" @input="setField('is_public_booking', $event)" class="select-primary" value-key="id" placeholder="Reservation style">
                <el-option v-for="(rs, i) in reservationStyles" class="select-primary" :label="rs.name" :value="{ id: rs.id, name: rs.name }" :key="'RS' + i" />
              </el-select>
            </base-input>
          </div>
        </div>

        <div class="toggle-row mb-2">
          <el-switch active-color="#e14eca" :value="slotData.cumulative_category_pricing" @change="setField('cumulative_category_pricing', $event)" />
          <label class="mb-0 ml-2 text-white" style="cursor: pointer" @click="setField('cumulative_category_pricing', !slotData.cumulative_category_pricing)">
            Category pricing is cumulative
            <el-popover popper-class="otc-pop" trigger="hover" placement="top">
              <div><div class="popover-body">Cumulative category pricing adjusts per-person rates based on the total group size across multiple categories.</div></div>
              <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
            </el-popover>
          </label>
        </div>
        <div class="toggle-row mb-2">
          <el-switch active-color="#e14eca" :value="slotData.hide_pricing_rate" @change="setField('hide_pricing_rate', $event)" />
          <label class="mb-0 ml-2 text-white" style="cursor: pointer" @click="setField('hide_pricing_rate', !slotData.hide_pricing_rate)">
            Hide pricing rate on booking site
          </label>
        </div>
        <div class="toggle-row mb-2">
          <el-switch active-color="#e14eca" :value="slotData.limit_purchase_category" @change="setField('limit_purchase_category', $event)" />
          <label class="mb-0 ml-2 text-white" style="cursor: pointer" @click="setField('limit_purchase_category', !slotData.limit_purchase_category)">
            Limit to one category per purchase
            <el-popover popper-class="otc-pop" trigger="hover" placement="top">
              <div><div class="popover-body">When enabled, customers can only purchase one pricing category at a time. When disabled, customers can combine multiple categories in a single purchase.</div></div>
              <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
            </el-popover>
          </label>
        </div>
      </drawer-section>

      <!-- Categories -->
      <drawer-section title="Pricing categories" icon="icon-tag">
        <div class="toggle-row mb-3">
          <el-switch active-color="#e14eca" :value="slotData.enable_multiple_durations" @change="onDurationsToggle($event)" />
          <label class="mb-0 ml-2 text-white" style="cursor: pointer" @click="onDurationsToggle(!slotData.enable_multiple_durations)">
            Enable multiple durations
            <el-popover popper-class="otc-pop" trigger="hover" placement="top">
              <div><div class="popover-body">Offer this slot at several lengths. Each duration has its own pricing categories and tiers. Customers pick the duration after choosing a time.</div></div>
              <i slot="reference" style="color: #1d8cf8" class="tim-icons el-icon-info"></i>
            </el-popover>
          </label>
        </div>
        <pricing-categories-editor :categories="categories" :errors="errors" :set-key="activeKey">
          <duration-pills v-if="slotData.enable_multiple_durations" class="mb-3"
                          :durations="durations" :value="activeMinutes"
                          @input="selectDuration" @add="addDuration" @remove="removeDuration" @set-default="setDefaultDuration" />
        </pricing-categories-editor>
      </drawer-section>

      <!-- Deposits -->
      <drawer-section title="Deposits" icon="icon-money-coins">
        <div class="toggle-row mb-2">
          <el-switch active-color="#e14eca" :value="slotData.allow_deposit" @change="setField('allow_deposit', $event)" />
          <label class="mb-0 ml-2 text-white" style="cursor: pointer" @click="setField('allow_deposit', !slotData.allow_deposit)">Allow deposit</label>
        </div>
        <div v-if="slotData.allow_deposit">
          <div class="toggle-row mb-2">
            <el-switch active-color="#e14eca" :value="slotData.require_deposit" @change="setField('require_deposit', $event)" />
            <label class="mb-0 ml-2 text-white" style="cursor: pointer" @click="setField('require_deposit', !slotData.require_deposit)">Require only deposit amount during checkout</label>
          </div>
          <div class="form-row">
            <div class="form-col">
              <base-input label="Deposit type">
                <el-select :value="slotData.deposit_type" @input="setField('deposit_type', $event)" class="select-primary" value-key="id" placeholder="Value type">
                  <el-option v-for="(type, i) in valueTypes" class="select-primary" :label="type.name" :value="{ id: type.id, name: type.name }" :key="'VT' + i" />
                </el-select>
              </base-input>
            </div>
            <div class="form-col">
              <label class="text-white">Deposit amount</label>
              <money-input
                :value="toNumber(slotData.deposit_amount)"
                :min="0"
                @input="v => setField('deposit_amount', v == null ? 0 : v)"
              />
            </div>
          </div>
        </div>
      </drawer-section>
    </template>
  </div>
</template>

<script setup lang="ts">
// Shared custom pricing editor for a slot or slot template. The `slotData` object
// is edited in place (same convention as the booking drawer views); the host
// keeps ownership of the object and decides when to persist it.
import { computed, getCurrentInstance, onMounted, ref, set } from 'vue'
import { validateSlotPricing } from '@/util/slotPricingValidation'

interface Option { id: number; name: string }
interface Tier { min_players: number | null; max_players: number | null; price: number | string | null }
interface Category {
  category: string
  pricing: Tier[]
  minimum_participants: number | null
  maximum_participants: number | null | undefined
  default_participants: number | null
}

const props = defineProps<{
  slotData: Record<string, any>
  pricingTypes: Option[]
  reservationStyles: Option[]
  valueTypes: Option[]
}>()

interface DurationSet {
  duration_minutes: number
  is_default: boolean
  position: number
  pricing_categories: Category[]
}

const instance = getCurrentInstance()
// Errors keyed per duration set: 'default' for the default duration, else the minutes
const errorsBySet = ref<Record<string, Record<number, any>>>({})

onMounted(() => {
  if (props.slotData.is_custom_pricing_enabled) seedDefaultCategory()
})

// Multiple durations. The default duration's categories stay in slotData.pricing_categories;
// every other duration owns its own array under slotData.durations[n].pricing_categories.
const defaultCategories = computed<Category[]>(() => {
  if (!Array.isArray(props.slotData.pricing_categories)) {
    set(props.slotData, 'pricing_categories', [])
  }
  return props.slotData.pricing_categories
})

const durations = computed<DurationSet[]>(() => {
  if (!Array.isArray(props.slotData.durations)) {
    set(props.slotData, 'durations', [])
  }
  return props.slotData.durations
})

const activeMinutes = computed<number | null>(() => {
  const v = props.slotData.active_duration_minutes
  return v === null || v === undefined ? null : Number(v)
})

const activeSet = computed<DurationSet | null>(() => {
  if (activeMinutes.value === null) return null
  return durations.value.find(d => Number(d.duration_minutes) === activeMinutes.value && !d.is_default) || null
})

const activeKey = computed(() => (activeSet.value ? String(activeSet.value.duration_minutes) : 'default'))

const categories = computed<Category[]>(() => {
  if (activeSet.value) {
    if (!Array.isArray(activeSet.value.pricing_categories)) set(activeSet.value, 'pricing_categories', [])
    return activeSet.value.pricing_categories
  }
  return defaultCategories.value
})

const errors = computed<Record<number, any>>(() => errorsBySet.value[activeKey.value] || {})

function setField(field: string, value: any) {
  set(props.slotData, field, value)
  if (field === 'is_custom_pricing_enabled' && value) seedDefaultCategory()
}

function ensureDefaultDuration() {
  if (!durations.value.some(d => d.is_default)) {
    durations.value.unshift({ duration_minutes: 60, is_default: true, position: 0, pricing_categories: [] })
  }
}

function onDurationsToggle(on: boolean) {
  set(props.slotData, 'enable_multiple_durations', !!on)
  if (on) ensureDefaultDuration()
  set(props.slotData, 'active_duration_minutes', null)
}

function selectDuration(minutes: number | null) {
  set(props.slotData, 'active_duration_minutes', minutes)
}

function addDuration(minutes: number) {
  ensureDefaultDuration()
  durations.value.push({
    duration_minutes: minutes,
    is_default: false,
    position: durations.value.length,
    pricing_categories: JSON.parse(JSON.stringify(defaultCategories.value))
  })
  set(props.slotData, 'active_duration_minutes', minutes)
}

function removeDuration(minutes: number) {
  const idx = durations.value.findIndex(d => Number(d.duration_minutes) === Number(minutes) && !d.is_default)
  if (idx < 0) return
  durations.value.splice(idx, 1)
  if (activeMinutes.value === Number(minutes)) set(props.slotData, 'active_duration_minutes', null)
}

function setDefaultDuration(minutes: number) {
  const next = durations.value.find(d => Number(d.duration_minutes) === Number(minutes) && !d.is_default)
  const current = durations.value.find(d => d.is_default)
  if (!next) return
  // The legacy array always holds the default duration's categories, so the two sets swap
  const nextCategories = next.pricing_categories
  set(next, 'pricing_categories', [])
  set(next, 'is_default', true)
  if (current) {
    set(current, 'is_default', false)
    set(current, 'pricing_categories', defaultCategories.value)
  }
  set(props.slotData, 'pricing_categories', nextCategories)
  set(props.slotData, 'active_duration_minutes', null)
  errorsBySet.value = {}
}

// A slot with custom pricing on and no categories has nothing to price; seed
// the same default category the schedule pages use for new slots.
function seedDefaultCategory() {
  if (defaultCategories.value.length > 0) return
  defaultCategories.value.push({
    category: 'Person',
    pricing: [{ min_players: 1, max_players: 2, price: 0 }],
    minimum_participants: 0,
    maximum_participants: null,
    default_participants: 0
  })
}

function toNumber(v: any): number | null {
  if (v === '' || v === null || v === undefined) return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}







/** Validate every duration's pricing; highlights errors, switches to the first failing set and notifies. Returns true when valid. */
function validate(): boolean {
  const enabled = !!props.slotData.is_custom_pricing_enabled
  const nextErrors: Record<string, Record<number, any>> = {}
  let valid = true
  let firstFailing: number | null | undefined = undefined
  const defaultResult = validateSlotPricing({ enabled, categories: defaultCategories.value })
  nextErrors['default'] = defaultResult.errors
  if (!defaultResult.valid) { valid = false; firstFailing = null }
  if (enabled && props.slotData.enable_multiple_durations) {
    for (const d of durations.value) {
      if (d.is_default) continue
      const r = validateSlotPricing({ enabled, categories: d.pricing_categories || [] })
      nextErrors[String(d.duration_minutes)] = r.errors
      if (!r.valid) {
        valid = false
        if (firstFailing === undefined) firstFailing = Number(d.duration_minutes)
      }
      if ((d.pricing_categories || []).length === 0) {
        valid = false
        if (firstFailing === undefined) firstFailing = Number(d.duration_minutes)
      }
    }
  }
  errorsBySet.value = nextErrors
  const result = { valid }
  if (!valid && firstFailing !== undefined) set(props.slotData, 'active_duration_minutes', firstFailing)
  if (!result.valid && instance) {
    (instance.proxy as any).$notify({
      type: 'danger',
      message: 'Fix the highlighted pricing errors before saving',
      icon: 'tim-icons icon-alert-circle-exc',
      timeout: 4000
    })
  }
  return result.valid
}

defineExpose({ validate })
</script>

<script lang="ts">
export default { name: 'SlotPricingEditor' }
</script>

<style lang="scss" scoped>
.section-label {
  text-transform: uppercase;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.05em;
  margin-bottom: 12px;
}

.form-row {
  display: flex;
  gap: 12px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0;
  }
}

.form-col {
  flex: 1;
}

.toggle-row {
  display: flex;
  align-items: center;

  label {
    font-size: 13px;
  }
}

</style>

<style lang="scss">
.slot-pricing-editor {
  .el-input-number--small {
    width: 100%;
  }


}

.white-content .slot-pricing-editor {
  .section-label { color: #525f7f; }
  .toggle-row label { color: #525f7f; }
  .text-white { color: #344675 !important; }
}
</style>
